import dayjs from 'dayjs';
import type {
  ChartYMode,
  MetricMode,
  MonthlyWarehouseMetric,
  PicqerLocation,
  PicqerLocationStockHistoryEntry,
  PicqerWarehouse,
  WarehouseSummaryRow,
} from './types';
import { ANALYSE_SINCE_DATE, ANALYSE_UNTIL_DATE } from './types';
import {
  ALL_MONTH_KEYS,
  buildActivePeriods,
  buildMonthKeys,
  computeLocationMonthlyMetrics,
  isUsedAtMonthEnd,
  occupiedFractionInMonth,
  parseChangedAt,
} from './locationMetricsCore';

export { ALL_MONTH_KEYS, buildMonthKeys };

export interface ComputeInput {
  warehouses: PicqerWarehouse[];
  locationsByWarehouse: Record<number, PicqerLocation[]>;
  /** Full location lists per warehouse (for summary totals); falls back to locationsByWarehouse */
  allLocationsByWarehouse?: Record<number, PicqerLocation[]>;
  historyByLocation: Record<number, PicqerLocationStockHistoryEntry[]>;
  preRangeStockByLocation: Record<number, number>;
  metricMode: MetricMode;
  monthFrom?: string;
  monthTo?: string;
}

export function computeUsedLocationPercent(input: ComputeInput): {
  monthlyMetrics: MonthlyWarehouseMetric[];
  chartData: Record<string, string | number>[];
  summaryRows: WarehouseSummaryRow[];
  monthKeys: string[];
} {
  const monthKeys = buildMonthKeys(
    input.monthFrom ?? ANALYSE_SINCE_DATE.slice(0, 7),
    input.monthTo ?? ANALYSE_UNTIL_DATE.slice(0, 7),
  );
  const rangeStart = dayjs(ANALYSE_SINCE_DATE.replace(' ', 'T'));
  const rangeEnd = dayjs(ANALYSE_UNTIL_DATE.replace(' ', 'T'));
  const monthlyMetrics: MonthlyWarehouseMetric[] = [];

  for (const warehouse of input.warehouses) {
    const locations = input.locationsByWarehouse[warehouse.idwarehouse] ?? [];
    const totalLocations = locations.length;
    if (totalLocations === 0) continue;

    const locationPeriods = new Map<number, ReturnType<typeof buildActivePeriods>>();
    for (const loc of locations) {
      const history = input.historyByLocation[loc.idlocation] ?? [];
      const preStock = input.preRangeStockByLocation[loc.idlocation] ?? 0;
      locationPeriods.set(loc.idlocation, buildActivePeriods(history, preStock, rangeStart, rangeEnd));
    }

    for (const monthKey of monthKeys) {
      const monthStart = dayjs(`${monthKey}-01`).startOf('month');
      const monthEnd = monthStart.endOf('month');
      const daysInMonth = monthEnd.date();

      let sumFractions = 0;
      let usedLocationCount = 0;

      for (const loc of locations) {
        const history = input.historyByLocation[loc.idlocation] ?? [];
        const preStock = input.preRangeStockByLocation[loc.idlocation] ?? 0;
        const periods = locationPeriods.get(loc.idlocation) ?? [];

        if (input.metricMode === 'month-end-snapshot') {
          const used = isUsedAtMonthEnd(history, preStock, monthEnd, rangeStart);
          sumFractions += used ? 1 : 0;
          if (used) usedLocationCount += 1;
        } else if (input.metricMode === 'any-time-in-month') {
          const fraction = occupiedFractionInMonth(periods, monthStart, monthEnd, rangeEnd);
          const used = fraction > 0;
          sumFractions += used ? 1 : 0;
          if (used) usedLocationCount += 1;
        } else {
          const fraction = occupiedFractionInMonth(periods, monthStart, monthEnd, rangeEnd);
          sumFractions += fraction;
          if (fraction > 0) usedLocationCount += 1;
        }
      }

      let usedPercent: number;
      if (input.metricMode === 'any-time-in-month' || input.metricMode === 'month-end-snapshot') {
        usedPercent = totalLocations > 0 ? Math.round((usedLocationCount / totalLocations) * 1000) / 10 : 0;
      } else {
        usedPercent = totalLocations > 0 ? Math.round((sumFractions / totalLocations) * 1000) / 10 : 0;
      }

      const occupiedLocationDays =
        input.metricMode === 'avg-month-occupation'
          ? Math.round(sumFractions * daysInMonth * 10) / 10
          : usedLocationCount * daysInMonth;

      monthlyMetrics.push({
        month: monthKey,
        warehouseId: warehouse.idwarehouse,
        warehouseName: warehouse.name,
        totalLocations,
        usedLocations: usedLocationCount,
        usedPercent,
        daysInMonth,
        occupiedLocationDays,
      });
    }
  }

  const chartData = monthKeys.map((month) => {
    const row: Record<string, string | number> = { month };
    for (const warehouse of input.warehouses) {
      const metric = monthlyMetrics.find(
        (m) => m.month === month && m.warehouseId === warehouse.idwarehouse,
      );
      row[`wh_${warehouse.idwarehouse}`] = metric?.usedPercent ?? 0;
      row[`wh_${warehouse.idwarehouse}_used`] = metric?.usedLocations ?? 0;
      row[`wh_${warehouse.idwarehouse}_total`] = metric?.totalLocations ?? 0;
      row[`wh_${warehouse.idwarehouse}_occupied_days`] = metric?.occupiedLocationDays ?? 0;
      row[`wh_${warehouse.idwarehouse}_days_in_month`] = metric?.daysInMonth ?? 0;
      row[`wh_${warehouse.idwarehouse}_name`] = warehouse.name;
    }
    return row;
  });

  const summaryRows: WarehouseSummaryRow[] = input.warehouses.map((warehouse) => {
    const warehouseMetrics = monthlyMetrics.filter((m) => m.warehouseId === warehouse.idwarehouse);
    const peakPercent = warehouseMetrics.reduce(
      (best, m) => (m.usedPercent > best.usedPercent ? m : best),
      warehouseMetrics[0] ?? { usedPercent: 0, month: monthKeys[0] ?? '', usedLocations: 0, totalLocations: 0 },
    );
    const peakLocations = warehouseMetrics.reduce(
      (best, m) => (m.usedLocations > best.usedLocations ? m : best),
      warehouseMetrics[0] ?? { usedLocations: 0, month: monthKeys[0] ?? '', usedPercent: 0, totalLocations: 0 },
    );

    const allLocs =
      input.allLocationsByWarehouse?.[warehouse.idwarehouse] ??
      input.locationsByWarehouse[warehouse.idwarehouse] ??
      [];

    return {
      warehouseId: warehouse.idwarehouse,
      warehouseName: warehouse.name,
      totalLocationsAll: allLocs.length,
      totalLocations: warehouseMetrics[0]?.totalLocations ?? 0,
      maxLocations: peakLocations.usedLocations,
      maxLocationsMonth: peakLocations.month,
      peakPercent: peakPercent.usedPercent,
      peakMonth: peakPercent.month,
    };
  });

  return { monthlyMetrics, chartData, summaryRows, monthKeys };
}

export function getChartYValue(
  metric: MonthlyWarehouseMetric | undefined,
  chartYMode: ChartYMode,
): number {
  if (!metric) return 0;
  return chartYMode === 'absolute' ? metric.usedLocations : metric.usedPercent;
}

export function buildChartDataForMode(
  chartData: Record<string, string | number>[],
  warehouses: PicqerWarehouse[],
  monthlyMetrics: MonthlyWarehouseMetric[],
  chartYMode: ChartYMode,
): Record<string, string | number>[] {
  return chartData.map((row) => {
    const out: Record<string, string | number> = { month: row.month };
    for (const wh of warehouses) {
      const metric = monthlyMetrics.find((m) => m.month === row.month && m.warehouseId === wh.idwarehouse);
      out[`wh_${wh.idwarehouse}`] = getChartYValue(metric, chartYMode);
      out[`wh_${wh.idwarehouse}_used`] = metric?.usedLocations ?? 0;
      out[`wh_${wh.idwarehouse}_total`] = metric?.totalLocations ?? 0;
      out[`wh_${wh.idwarehouse}_pct`] = metric?.usedPercent ?? 0;
      out[`wh_${wh.idwarehouse}_occupied_days`] = metric?.occupiedLocationDays ?? 0;
      out[`wh_${wh.idwarehouse}_days_in_month`] = metric?.daysInMonth ?? 0;
    }
    return out;
  });
}

export { computeLocationMonthlyMetrics };
